import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import AdminDataTable from '../../components/admin/AdminDataTable';
import {
  formatAdminAnswerTime,
  formatAdminDate,
  formatAdminDuration,
} from '../../components/admin/adminFormatting';
import AdminPagination from '../../components/admin/AdminPagination';
import MeetingStatusBadge from '../../components/admin/MeetingStatusBadge';
import { PageHeader, Select } from '@copilot/ui';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import * as adminApi from '../../services/adminApi';

const PAGE_SIZE = 20;

// scheduled/active/paused/ended - models/meeting.py's MeetingStatus.
const STATUS_FILTERS = [
  { value: '', label: 'All Statuses' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'active', label: 'Running' },
  { value: 'paused', label: 'Paused' },
  { value: 'ended', label: 'Ended' },
];

// microphone/meeting_tab/mixed - models/meeting.py's MeetingAudioSource.
const AUDIO_SOURCE_FILTERS = [
  { value: '', label: 'All Audio Sources' },
  { value: 'microphone', label: 'Microphone' },
  { value: 'meeting_tab', label: 'Meeting Tab' },
  { value: 'mixed', label: 'Mixed' },
];

const EMPTY_FILTERS = {
  // Soft-deleted meetings are hidden by default; this toggle asks the
  // backend to include them (they render with a Deleted badge).
  includeDeleted: false,
  status: '',
  audioSource: '',
  userId: '',
  sourceLanguage: '',
  targetLanguage: '',
  sttProvider: '',
  ttsProvider: '',
  translationProvider: '',
  dateFrom: '',
  dateTo: '',
};

const COLUMNS = (navigate) => [
  { key: 'id', label: 'Meeting ID', render: (m) => `#${m.id}` },
  {
    key: 'title',
    label: 'Title',
    render: (m) => (
      <button
        type="button"
        className="admin-table-name"
        title={m.title || undefined}
        onClick={() => navigate(`/admin/meetings/${m.id}`)}
      >
        {m.title || `Meeting #${m.id}`}
      </button>
    ),
  },
  {
    key: 'owner',
    label: 'Owner',
    render: (m) => (
      <>
        <div>{m.owner.full_name}</div>
        <div className="admin-table-subtext">{m.owner.email}</div>
      </>
    ),
  },
  {
    key: 'status',
    label: 'Status',
    render: (m) => <MeetingStatusBadge status={m.status} deletedAt={m.deleted_at} />,
  },
  { key: 'created_at', label: 'Created', render: (m) => formatAdminDate(m.created_at) },
  { key: 'duration_seconds', label: 'Duration', render: (m) => formatAdminDuration(m.duration_seconds) },
  {
    key: 'ai_answer_time',
    label: 'Avg. AI Answer Time',
    render: (m) => formatAdminAnswerTime(m.ai_answer_timing?.average_ms),
  },
  {
    key: 'audio_source',
    label: 'Audio Source',
    render: (m) => AUDIO_SOURCE_FILTERS.find((o) => o.value === m.audio_source)?.label || m.audio_source,
  },
];

function AdminMeetingsPage() {
  const navigate = useNavigate();

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);

  const [meetings, setMeetings] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const setFilter = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));

  const setCheckboxFilter = (key) => (e) =>
    setFilters((f) => ({ ...f, [key]: e.target.checked }));

  // Any filter change invalidates the current page - see
  // AdminUsersPage.jsx for the same reasoning.
  useEffect(() => {
    setPage(1);
  }, [filters]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setIsLoading(true);
      setError('');

      try {
        const data = await adminApi.getAdminMeetings({
          status: filters.status || undefined,
          audio_source: filters.audioSource || undefined,
          user_id: filters.userId || undefined,
          source_language: filters.sourceLanguage.trim() || undefined,
          target_language: filters.targetLanguage.trim() || undefined,
          stt_provider: filters.sttProvider.trim() || undefined,
          tts_provider: filters.ttsProvider.trim() || undefined,
          translation_provider: filters.translationProvider.trim() || undefined,
          date_from: filters.dateFrom || undefined,
          date_to: filters.dateTo || undefined,
          include_deleted: filters.includeDeleted || undefined,
          page,
          page_size: PAGE_SIZE,
        });

        if (!cancelled) {
          setMeetings(data.items);
          setTotal(data.total);
          setTotalPages(data.total_pages);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err.status === 403
              ? 'You do not have permission to view meetings.'
              : err.message || 'Unable to load meetings.'
          );
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [filters, page]);

  // Silent re-read: no loading/error state, last good data kept on failure.
  const refreshMeetings = useCallback(
    async ({ isStale }) => {
      const data = await adminApi.getAdminMeetings({
        status: filters.status || undefined,
        audio_source: filters.audioSource || undefined,
        user_id: filters.userId || undefined,
        source_language: filters.sourceLanguage.trim() || undefined,
        target_language: filters.targetLanguage.trim() || undefined,
        stt_provider: filters.sttProvider.trim() || undefined,
        tts_provider: filters.ttsProvider.trim() || undefined,
        translation_provider: filters.translationProvider.trim() || undefined,
        date_from: filters.dateFrom || undefined,
        date_to: filters.dateTo || undefined,
        include_deleted: filters.includeDeleted || undefined,
        page,
        page_size: PAGE_SIZE,
      });

      if (!isStale()) {
        setMeetings(data.items);
        setTotal(data.total);
        setTotalPages(data.total_pages);
      }
    },
    [filters, page]
  );

  useAutoRefresh(refreshMeetings);

  return (
    <div>
      <PageHeader title="Meetings" description="Monitor and manage platform meetings." />

      <div className="admin-toolbar">
        <div className="admin-toolbar-filter">
          <Select value={filters.status} onChange={setFilter('status')} aria-label="Filter by status">
            {STATUS_FILTERS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
        </div>
        <div className="admin-toolbar-filter">
          <Select value={filters.audioSource} onChange={setFilter('audioSource')} aria-label="Filter by audio source">
            {AUDIO_SOURCE_FILTERS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
        </div>
        <div className="admin-toolbar-filter">
          <input className="input"
            type="number"
            min="1"
            placeholder="User ID"
            value={filters.userId}
            onChange={setFilter('userId')}
            aria-label="Filter by user ID"
          />
        </div>
        <div className="admin-toolbar-filter">
          <input className="input"
            placeholder="Source language"
            value={filters.sourceLanguage}
            onChange={setFilter('sourceLanguage')}
            aria-label="Filter by source language"
          />
        </div>
        <div className="admin-toolbar-filter">
          <input className="input"
            placeholder="Target language"
            value={filters.targetLanguage}
            onChange={setFilter('targetLanguage')}
            aria-label="Filter by target language"
          />
        </div>
        <div className="admin-toolbar-filter">
          <input className="input"
            placeholder="STT provider"
            value={filters.sttProvider}
            onChange={setFilter('sttProvider')}
            aria-label="Filter by STT provider"
          />
        </div>
        <div className="admin-toolbar-filter">
          <input className="input"
            placeholder="TTS provider"
            value={filters.ttsProvider}
            onChange={setFilter('ttsProvider')}
            aria-label="Filter by TTS provider"
          />
        </div>
        <div className="admin-toolbar-filter">
          <input className="input"
            placeholder="Translation provider"
            value={filters.translationProvider}
            onChange={setFilter('translationProvider')}
            aria-label="Filter by translation provider"
          />
        </div>
        <div className="admin-toolbar-filter">
          <input className="input"
            type="date"
            value={filters.dateFrom}
            onChange={setFilter('dateFrom')}
            aria-label="Filter by date from"
          />
        </div>
        <div className="admin-toolbar-filter">
          <input className="input"
            type="date"
            value={filters.dateTo}
            onChange={setFilter('dateTo')}
            aria-label="Filter by date to"
          />
        </div>
        <label className="admin-plan-toggle" htmlFor="meetings-include-deleted">
          <input
            id="meetings-include-deleted"
            type="checkbox"
            checked={filters.includeDeleted}
            onChange={setCheckboxFilter('includeDeleted')}
          />
          Include deleted
        </label>
      </div>

      {error ? (
        <div className="form-alert">{error}</div>
      ) : (
        <>
          <AdminDataTable
            columns={COLUMNS(navigate)}
            rows={meetings}
            getRowKey={(m) => m.id}
            onRowClick={(m) => navigate(`/admin/meetings/${m.id}`)}
            isLoading={isLoading}
            loadingLabel="Loading meetings..."
            emptyTitle="No meetings found."
            emptyDescription="Try a different filter."
          />

          {!isLoading && (
            <AdminPagination
              page={page}
              totalPages={totalPages}
              total={total}
              itemLabel="meetings"
              onPageChange={setPage}
              ariaLabel="Meeting list pages"
            />
          )}
        </>
      )}
    </div>
  );
}

export default AdminMeetingsPage;
